import {Box} from 'platform/foundation';

import {useState} from 'react';

import {mergeRight} from 'ramda';

import {DmsDataGrid} from '@dms/shared';

import type {RequiredTestIdProps} from 'shared/web';

import type {FormToDataGridConnectorProps, QueryFilterObject} from 'features/eag-grid';

import {ARTICLE_ID_FILTER_KEY} from '../constants/ARTICLE_ID_FILTER_KEY';
import {AbcXyzArticleFilter} from './AbcXyzArticleFilter';

interface AbcXyzDetailGridProps extends RequiredTestIdProps {
  gridCode: string;
  articleId: string;
}

export function AbcXyzDetailGrid(props: AbcXyzDetailGridProps) {
  const externalFilterId = `${props.gridCode}-external-filter`;

  // The grid renders `filterComponent` as a component, so its identity has to stay stable or the
  // filter would remount (and re-apply itself) on every render. The article never changes for the
  // life of a detail grid — each row opens its own dialog — so it is captured once.
  const [filterComponent] = useState(() => (connector: FormToDataGridConnectorProps) => (
    <AbcXyzArticleFilter {...connector} articleId={props.articleId} />
  ));

  /**
   * The external filter reaches the grid only after it has loaded and mounted the filter, and the
   * `articleId` column is not mandatory on the backend. Without this the first data request would
   * carry no article and return the rows of every article (hundreds of thousands). The grid
   * applies a `queryModifier` filter to every request, the first one included.
   */
  const queryModifier = (filter: QueryFilterObject) =>
    mergeRight(filter, {[ARTICLE_ID_FILTER_KEY]: props.articleId});

  return (
    <Box height="100%" minHeight={0}>
      <div id={externalFilterId} />
      <Box height="100%" minHeight={0}>
        <DmsDataGrid
          gridCode={props.gridCode}
          externalFilterId={externalFilterId}
          filterComponent={filterComponent}
          queryModifier={queryModifier}
          data-testid={props['data-testid']}
        />
      </Box>
    </Box>
  );
}
